import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  OnInit,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';
import { map, Observable, shareReplay, take } from 'rxjs';
import { UserState } from '@ups/user';
import {
  glAccountMatchesQuery,
  SearchVpGlAccountService,
} from '@ups/tool-tracker';
import { DynamicTypeaheadWebBaseComponent } from '../base/dynamic-typeahead.web-base-component';

const SEARCH_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DynamicTypeaheadGlAccountComponent),
  multi: true,
};

/** WI 34966: Viewpoint GL account search, scoped to the signed-in user's default company. */
@Component({
  selector: 'ups-dynamic-typeahead-gl-account',
  templateUrl: 'dynamic-typeahead-gl-account.component.html',
  providers: [SEARCH_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-commit-allow: declared in the dynamic-render NgModule with its typeahead siblings (WI 34966)
  standalone: false,
})
export class DynamicTypeaheadGlAccountComponent
  extends DynamicTypeaheadWebBaseComponent
  implements OnInit
{
  private readonly store = inject(Store);
  private readonly glAccountService = inject(SearchVpGlAccountService);
  /** The user's default Viewpoint company; the API resolves the GL company it posts to. */
  private readonly company$: Observable<number | null | undefined> =
    this.store.pipe(
      UserState.selectCurrentTruthy(),
      take(1),
      map((myInfo) => myInfo?.Data?.VPCompanyID),
      shareReplay(1)
    );

  override ngOnInit(): void {
    this.queryFilter = glAccountMatchesQuery;
    // valueProperty is pinned to 'glAcct' by type in createConfigGroup (dynamicValuePropertyFor).
    // Set before super.ngOnInit so its reopen prefill searches the saved account.
    this.searchQuery = (query: unknown) =>
      this.glAccountService.searchForCompany(`${query ?? ''}`, this.company$);
    super.ngOnInit();
    // [value]-bound template: repaint on every control write (PR 4539 thread 56562).
    this.bindViewToControl();
  }

  /**
   * The shared base writes isLoading and searchError inside the search's subscribe, which does
   * not mark an OnPush view. Mark it once each search settles so the spinner and error repaint.
   */
  override filterChangeItem(
    ...args: Parameters<DynamicTypeaheadWebBaseComponent['filterChangeItem']>
  ) {
    return super
      .filterChangeItem(...args)
      .finally(() => this.cdr?.markForCheck());
  }
}
